/* eslint-disable no-use-before-define */
/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-shadow */
import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, InlineNotification } from 'uxd-design-system-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import ResultsPageProps from '~types/pages/ResultsPageProps';
import NotificationContent from '../../../types/NotificationContent';
import ValueCardCarousel from '../../../components/ValueCardCarousel/ValueCardCarousel';
import getStatementText from '../../../utils/getStatementText';
import getText from '../../../assets/locale/locale';
import ValueCardPodium from '../../../components/ValueCardPodium/ValueCardPodium';
import LocaleContext from '../../../contexts/LocaleContext';
import validTime from '../../../utils/validTime/validTime';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';

import './ResultsPage.scss';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import DownloadButton from '../../../components/DownloadButton/DownloadButton';
import CopyLinkButton from '../../../components/CopyLinkButton/CopyLinkButton';
import SendResultsButton from '../../../components/SendResultsButton/SendResultsButton';
import GoToFinancialFoundationsButton from '../../../components/GoToFinancialFoundationsButton/GoToFinancialFoundationsButton';
import getLearnMoreContent from '../../../utils/getLearnMoreContent';
import getLearnMoreLinks from '../../../utils/getLearnMoreLinks';
import DnDContainer from '../../../components/DragAndDrop/DnDContainer';
import ShareLinkIcon from '../../../components/ShareLinkIcon/ShareLinkIcon';
import SocialMediaButton from '../../../components/SocialMediaButton/SocialMediaButton';
import useQuery from '../../../utils/useQuery/useQuery';
import ResultsObject from '../../../types/ResultsObject';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';
import { useLocation } from 'react-router-dom';

const ResultsPage = (props: ResultsPageProps) => {
  const {
    contactID,
    contactName,
    financialAdvisorID,
    entityID,
    timestamp,
    sessionId,
    valueList,
    finalResultsIndex,
    restartCallback,
    callerApp,
    displaySendToFAButton,
    faInfo,
  } = props;

  const initialResultsStatementIds = finalResultsIndex.map((index: number) => {
    const value = valueList.find(value => index === value.indexId);
    return value.statementId;
  });
  const { dataLayerInfo } = useDataLayerInfo();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationContent, setNotificationContent] = useState<NotificationContent>({ type: null, message: null });
  const [displaySendResults, setDisplaySendResults] = useState(false);
  const [coldContactData, setColdContactData] = useState<ColdContact>(null);
  const [userComments, setUserComments] = useState('');
  const [cardClassName, setCardClassName] = useState('value-card');
  const [finalResultsStatementIds, setFinalResultsStatementIds] = useState(
    initialResultsStatementIds
  );
  const [sendResultsButtonDisabled, setSendResultsButtonDisabled] = useState(
    false
  );
  const topOfResults = useRef<HTMLHeadingElement>();
  const locId = useContext(LocaleContext);
  const resultsJSON = createResultsJSON();
  const displayFAName = !!faInfo.faFirstName && !!faInfo.faLastName;
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;
  const sendResultsToFaText = getText(locId, 'sendResultsToFA', 'resultsPage');
  const faNameDefaultText = getText(locId, 'faDefaultText', 'resultsPage');
  const whatsNextSendToFaHeadingText = getText(
    locId,
    'whatNextSendToFaHeading',
    'resultsPage'
  );
  const whatsNextSendToFaText = getText(
    locId,
    'whatNextSendToFA',
    'resultsPage'
  );
  const yourFaNameDefaultText = getText(
    locId,
    'yourFaDefaultText',
    'resultsPage'
  );
  const urlQuery = useQuery();

  const isTouchDevice = () => {
    return 'ontouchstart' in window;
  };

  const isFinancialFoundationsUser = () => {
    return (
      (urlQuery.get('faNo') || faInfo.faNo) &&
      urlQuery.get('conId') &&
      urlQuery.get('utm_source') &&
      urlQuery.get('utm_source').toUpperCase() === 'FF'
    );
  };

  const isSendToFAButtonDisplayed = () => {
    return (
      displaySendToFAButton &&
      !isFinancialFoundationsUser() &&
      (!contactID || (contactID && validTime(timestamp)))
    );
  };

  const isFindAnFAButtonDisplayed = () => {
    return (
      callerApp === 'StartingPoint' &&
      (!financialAdvisorID || financialAdvisorID === '0') &&
      (!entityID || entityID === '0')
    );
  };

  const getValueStatementHeadersString = () => {
    const valueStatementHeaderArray = finalResultsStatementIds.map(
      (statementId: number) => {
        const value = valueList.find(
          value => statementId === value.statementId
        );
        return statementId === 15 ? 'Custom Text' : value.statementText;
      }
    );
    return valueStatementHeaderArray.join('|');
  };

  useEffect(() => {
    addDataLayerTrigger('value-statements-displayed', {
      'value-statements-as-string': getValueStatementHeadersString(),
    });
  }, [finalResultsStatementIds]);

  const updatedFaInfo = {
    ...dataLayerInfo,
    page_type: "results-page",
  }

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'resultsPage');
  const pageReferrer = previousPath ? `${window.location.origin}${previousPath}${currentLocation.search}` : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    'pagePath': currentLocation.pathname,
    'pageTitle': pageTitle,
    'page_referrer': pageReferrer,
    'external_referrer': window.document.referrer && window.document.referrer.trim() !== '' ? window.document.referrer : undefined,
    'page_url': pageURL
  };

  useEffect(() => {
    setPreviousPath(currentLocation.pathname);
    addDataLayerTrigger("dataLayer-initialized", updatedFaInfo);
    addDataLayerTrigger('VirtualPageview', virtualPageInfo);
    if (topOfResults.current !== undefined) {
      topOfResults.current.focus();
    }
    ariaHideCarouselIndicators();
    document.title = pageTitle;
    addDataLayerTrigger('view-results', {});

    if (isSendToFAButtonDisplayed()) {
      setDisplaySendResults(true);
    }
    setTimeout(removeFocus, 1000);
  }, []);

  function ariaHideCarouselIndicators() {
    const ol = document.querySelector('.carousel-indicators');
    ol.setAttribute('aria-hidden', 'true');
  }

  function removeFocus() {
    const heading = document.getElementById('heading-no-focus');
    heading.removeAttribute('tabIndex');
  }

  function createResultsJSON() {
    const resultsObj: Record<string, ResultsObject> = {};
    finalResultsStatementIds.forEach((result: number, index: number) => {
      const statementId = result;
      const statementText = getStatementText(result, valueList);
      const learnMoreContent = getLearnMoreContent(result, valueList);
      const learnMoreLinks = getLearnMoreLinks(result, valueList);
      const keyName = (index + 1).toString();
      resultsObj[keyName] = {
        statementId,
        statementText,
        learnMoreContent,
        learnMoreLinks,
      };
    });
    return resultsObj;
  }

  const handleDefaultDisplayNameText = () => {
    return sendResultsToFaText.replace('%FA_NAME%', faNameDefaultText);
  };
  const handleDisplayFaNameText = () => {
    if (displayFAName) {
      return sendResultsToFaText.replace('%FA_NAME%', faFullName);
    }
    return handleDefaultDisplayNameText();
  };

  const handleWhatsNextSendToFaHeadingText = () => {
    if (displayFAName) {
      return whatsNextSendToFaHeadingText.replace('%FA_NAME%', faFullName);
    }
    return whatsNextSendToFaHeadingText.replace(
      '%FA_NAME%',
      yourFaNameDefaultText
    );
  };

  const handleWhatsNextSendToFaText = () => {
    if (displayFAName) {
      return whatsNextSendToFaText.replace('%FA_NAME%', faFullName);
    }
    return whatsNextSendToFaText.replace('%FA_NAME%', yourFaNameDefaultText);
  };

  return (
    <div data-testid="ResultsPage" className="ResultsPage">
      {finalResultsStatementIds.length > 0 ? (
        <>
          <div className="youDidIt">
            <div className="banner">
              <h1
                id="heading-no-focus"
                className="heading--semi-bold"
                ref={topOfResults}
                tabIndex={-1}
              >
                {contactName.firstName
                  ? getText(
                    locId,
                    'howYouRankedHeading',
                    'resultsPage'
                  ).replace('%CONTACT_FIRSTNAME%', contactName.firstName)
                  : getText(
                    locId,
                    'howYouRankedHeadingContactUnkown',
                    'resultsPage'
                  )}
              </h1>
            </div>
            <p>{getText(locId, 'topThreePriorities', 'resultsPage')}</p>
            <ol className="visually-hidden">
              <li key={finalResultsStatementIds[0]}>
                {getStatementText(finalResultsStatementIds[0], valueList)}
              </li>
              <li key={finalResultsStatementIds[1]}>
                {getStatementText(finalResultsStatementIds[1], valueList)}
              </li>
              <li key={finalResultsStatementIds[2]}>
                {getStatementText(finalResultsStatementIds[2], valueList)}
              </li>
            </ol>
          </div>
          <div className="results_topThree">
            <div
              className="results_podium"
              data-testid="results_podium"
              aria-hidden="true"
            >
              <ValueCardPodium
                valueList={valueList}
                finalResults={finalResultsStatementIds}
                cardClassName={cardClassName}
                setCardClassName={setCardClassName}
              />
            </div>
            <div className="results_carousel" data-testid="results_carousel">
              <ValueCardCarousel
                valueList={valueList}
                finalResults={finalResultsStatementIds}
                cardClassName={cardClassName}
                setCardClassName={setCardClassName}
              />
            </div>
          </div>
          {notificationOpen ? (
            <div className="inlineNotification_container">
              <InlineNotification
                type={notificationContent.type}
                text={notificationContent.message}
                onClose={() => {
                  setNotificationOpen(false);
                }}
              />
            </div>
          ) : null}
          <div className="nowWhat">
            <div className="action_btns">
              {displaySendResults ? (
                <SendResultsButton
                  coldContactData={coldContactData}
                  contactName={contactName}
                  setColdContactData={setColdContactData}
                  setNotificationContent={setNotificationContent}
                  setNotificationOpen={setNotificationOpen}
                  userComments={userComments}
                  setUserComments={setUserComments}
                  faInfo={faInfo}
                  sessionId={sessionId}
                  resultsJSON={resultsJSON}
                  sendResultsButtonDisabled={sendResultsButtonDisabled}
                  setSendResultsButtonDisabled={setSendResultsButtonDisabled}
                  buttonText={handleDisplayFaNameText()}
                />
              ) : null}

              {!isFinancialFoundationsUser() ? (
                <DownloadButton
                  setNotificationContent={setNotificationContent}
                  setNotificationOpen={setNotificationOpen}
                  displaySendToFAButton={displaySendResults}
                  resultsJSON={resultsJSON}
                  coldContactData={coldContactData}
                  userComments={userComments}
                  label={getText(locId, 'downloadPdf', 'resultsPage')}
                />
              ) : null}

              {isFinancialFoundationsUser() ? (
                <GoToFinancialFoundationsButton
                  setNotificationContent={setNotificationContent}
                  setNotificationOpen={setNotificationOpen}
                  sessionId={sessionId}
                  resultsJSON={resultsJSON}
                  faInfo={faInfo}
                />
              ) : null}

              {isFindAnFAButtonDisplayed() ? (
                <a
                  href={getText(locId, 'findFALink', 'resultsPage')}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={-1}
                >
                  <Button
                    label={getText(locId, 'findFAButton', 'resultsPage')}
                    onClick={() => { }}
                    attributes={{
                      value: '',
                      id: 'find-fa',
                      'data-testid': 'find-fa',
                    }}
                  />
                </a>
              ) : null}
              <Button
                label={getText(locId, 'restartQuiz', 'resultsPage')}
                onClick={() => {
                  addDataLayerTrigger('restart-quiz', {});
                  restartCallback();
                }}
                attributes={{
                  value: '',
                  id: 'restart-button',
                }}
                styleVariant="secondary"
              />
            </div>
          </div>
          <div className="dnd-header">
            <h3>{getText(locId, 'dndHeading', 'resultsPage')}</h3>
            <p>{getText(locId, 'dndMessage', 'resultsPage')}</p>
          </div>
          <DndProvider
            backend={isTouchDevice() ? TouchBackend : HTML5Backend}
          >
            <DnDContainer
              finalResultsStatementIds={finalResultsStatementIds}
              valueList={valueList}
              setFinalResultsStatementIds={setFinalResultsStatementIds}
            />
          </DndProvider>
        </>
      ) : null}
      {!isFinancialFoundationsUser() ? (
        <div className="whatNext">
          <h2>{getText(locId, 'whatNext', 'resultsPage')}</h2>
          <div className="cta_container">
            {displaySendResults ? (
              <div className="whatNext_container">
                <h3>{handleWhatsNextSendToFaHeadingText()}</h3>
                <p>{handleWhatsNextSendToFaText()}</p>
                <div className="action_btns">
                  <SendResultsButton
                    coldContactData={coldContactData}
                    contactName={contactName}
                    setColdContactData={setColdContactData}
                    setNotificationContent={setNotificationContent}
                    setNotificationOpen={setNotificationOpen}
                    userComments={userComments}
                    setUserComments={setUserComments}
                    faInfo={faInfo}
                    sessionId={sessionId}
                    resultsJSON={resultsJSON}
                    sendResultsButtonDisabled={sendResultsButtonDisabled}
                    setSendResultsButtonDisabled={
                      setSendResultsButtonDisabled
                    }
                    buttonText={handleDefaultDisplayNameText()}
                  />
                </div>
              </div>
            ) : (
              <div className="whatNext_container">
                <h3>
                  {getText(locId, 'whatNextDownloadHeading', 'resultsPage')}
                </h3>
                <p>{getText(locId, 'whatNextDownload', 'resultsPage')}</p>
                <div className="action_btns">
                  <DownloadButton
                    setNotificationContent={setNotificationContent}
                    setNotificationOpen={setNotificationOpen}
                    displaySendToFAButton={displaySendResults}
                    resultsJSON={resultsJSON}
                    coldContactData={coldContactData}
                    userComments={userComments}
                    label={getText(locId, 'downloadResults', 'resultsPage')}
                  />
                </div>
              </div>
            )}
            <div className="shareQuiz_container">
              <h3>{getText(locId, 'shareQuizHeading', 'resultsPage')}</h3>
              <p>{getText(locId, 'shareQuizMessage', 'resultsPage')}</p>
              <div className="action_btns_container">
                <div className="copyLink_action_btn">
                  <CopyLinkButton />
                </div>
                <div className="shareQuiz_action_btn">
                  <p>{getText(locId, 'shareLink', 'resultsPage')}</p>
                  <SocialMediaButton
                    icon="/public/images/linkedin-icon.png"
                    socialMediaLink={getText(
                      locId,
                      'linkedInUrl',
                      'resultsPage'
                    )}
                    title={getText(
                      locId,
                      'socialMediaTitle',
                      'resultsPage'
                    ).replace('%APP_NAME%', 'LinkedIn')}
                  />
                  <SocialMediaButton
                    icon="/public/images/facebook-icon.png"
                    socialMediaLink={getText(
                      locId,
                      'faceBookUrl',
                      'resultsPage'
                    )}
                    title={getText(
                      locId,
                      'socialMediaTitle',
                      'resultsPage'
                    ).replace('%APP_NAME%', 'Facebook')}
                  />
                  <ShareLinkIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="footer">
        <div className="disclaimer">
          <p className="microcopy--2">
            {getText(locId, 'resultsPageDisclosure', 'resultsPage')}
          </p>
        </div>
      </div>
    </div>
  );
};

ResultsPage.defaultProps = {
  contactID: '',
  contactName: null,
  financialAdvisorID: '',
  entityID: '',
  timestamp: '',
  callerApp: '',
  faInfo: null,
};

ResultsPage.propTypes = {
  contactID: PropTypes.string,
  contactName: PropTypes.objectOf(PropTypes.string),
  financialAdvisorID: PropTypes.string,
  entityID: PropTypes.string,
  timestamp: PropTypes.string,
  sessionId: PropTypes.string.isRequired,
  valueList: PropTypes.arrayOf(PropTypes.object).isRequired,
  finalResultsIndex: PropTypes.arrayOf(PropTypes.number).isRequired,
  restartCallback: PropTypes.func.isRequired,
  callerApp: PropTypes.string,
  displaySendToFAButton: PropTypes.bool.isRequired,
  faInfo: PropTypes.shape({
    locale: PropTypes.string,
    environment: PropTypes.string,
    sessionId: PropTypes.string,
    faNo: PropTypes.string,
    fa_entity_id: PropTypes.string,
  }).isRequired,
};

export default ResultsPage;
